import { FeesDto } from 'api/borrower'
import { ComparateurPricingStatus } from 'api/pricing'
import { MainBorrowerPricingAction } from './actions/types'
import { MainBorrowerPricingState } from './types'

const BORROWER_PRO_MODE_KEY = 'borrower-pricing-pro-mode'

export const initialState: MainBorrowerPricingState = {
  insured: null,
  borrowerId: null,
  borrowerProjectId: null,
  tabs: [],
  loansConfigurations: [],
  contractFunnels: [],
  globalWarningMessages: [],
  adjustmentsConfig: {
    borrowerCommission: '40/10',
    fees: [],
    modeLissageFraisIntermediation: 'LISSAGE_1ER_ANNEE'
  },
  filters: [],
  fees: [],
  borrowersCommissions: [],
  guaranteesReferential: [],
  pendingLoanConfigs: [],
  isProMode: localStorage.getItem(BORROWER_PRO_MODE_KEY) === 'true',
  isPriceLoading: false,
  isContractFunnelsLoading: true,
  shoppingCart: [],
  isLoading: true,
  error: null
}

export const mainBorrowerPricingReducer = (
  state: MainBorrowerPricingState,
  action: MainBorrowerPricingAction
): MainBorrowerPricingState => {
  switch (action.type) {
    case 'INIT_PRICING_PROCESS':
      // Reset shoppingCart et contractFunnels à chaque init pour éviter la fuite d'état
      // entre emprunteurs du même projet lors du switch (sinon on voit brièvement les
      // funnels de l'emprunteur précédent marqués "dans le panier").
      return {
        ...state,
        ...action.payload,
        shoppingCart: [],
        contractFunnels: [],
        error: null
      }

    case 'SET_BORROWER_FUNNELS_DATA':
      return {
        ...state,
        filters: action.payload.filters || [],
        fees: action.payload.fees || [],
        borrowersCommissions: action.payload.borrowersCommissions || [],
        borrowerCommissionValue: action.payload.borrowerCommissionValue,
        guaranteesReferential: action.payload.guaranteesReferential || [],
        adjustmentsConfig: action.payload.borrowerCommissionValue
          ? { ...state.adjustmentsConfig, borrowerCommission: action.payload.borrowerCommissionValue }
          : state.adjustmentsConfig,
        error: null
      }

    case 'UPDATE_PENDING_LOAN_CONFIGS':
      return {
        ...state, pendingLoanConfigs: action.payload, error: null
      }

    case 'UPDATE_CONTRACT_FUNNELS': {
      // Réappliquer les fees globaux du state local sur les funnels reçus de l'API
      // Mais préserver les fees locaux des funnels en mode édition (fees différents du global)
      const globalFeesMap: Record<string, number> = {}
      state.adjustmentsConfig.fees?.forEach(f => {
        globalFeesMap[f.code] = f.value
      })

      const newFunnels = action.payload.map(funnel => {
        if (Object.keys(globalFeesMap).length === 0) return funnel

        // Funnel en mode édition (géré par le back) : ne pas toucher ses fees
        if (funnel.isEdition) return funnel

        // Funnel non-édité : réappliquer les fees globaux
        return {
          ...funnel,
          fees: funnel.fees?.map((fee: FeesDto) =>
            fee.code in globalFeesMap ? { ...fee, value: globalFeesMap[fee.code] } : fee
          )
        }
      })

      return {
        ...state, contractFunnels: newFunnels, error: null
      }
    }

    case 'UPDATE_GLOBAL_WARNING_MESSAGES':
      return {
        ...state, globalWarningMessages: action.payload, error: null
      }

    case 'UPDATE_SHOPPING_CART':
      return {
        ...state,
        shoppingCart: action.payload,
        error: null
      }

    case 'DELETE_ITEM_IN_SHOPPING_CART':
      return {
        ...state,
        shoppingCart: state.shoppingCart
          .filter(item => item.contractFunnel.hash !== action.payload)
        ,
        error: null
      };

    case 'TOGGLE_LOAN_IS_ACTIVE':
      return {
        ...state,
        loansConfigurations: state.loansConfigurations.map(config =>
          config.insuranceConfig.borrowerLoanId === action.payload
            ? {
              ...config,
              insuranceConfig: {
                ...config.insuranceConfig,
                isActive: !config.insuranceConfig.isActive
              }
            }
            : config
        ),
        pendingLoanConfigs: state.pendingLoanConfigs.map(config =>
          config.borrowerLoanId === action.payload
            ? { ...config, isActive: !config.isActive }
            : config
        ),
        error: null
      }

    case 'TOGGLE_IS_PRO_MODE': {
      const newIsProMode = !state.isProMode
      localStorage.setItem(BORROWER_PRO_MODE_KEY, String(newIsProMode))
      return { ...state, isProMode: newIsProMode, error: null }
    }
    case 'UPDATE_ADJUSTMENTS_CONFIG':
      return { ...state, adjustmentsConfig: action.payload, error: null }

    case 'TOGGLE_FILTER_ITEM':
      return {
        ...state,
        filters: state.filters.map(filter =>
          filter.type === action.payload.filterId
            ? {
              ...filter,
              items: filter.items.map(item =>
                item.id === action.payload.itemId
                  ? { ...item, value: !item.value }
                  : item
              )
            }
            : filter
        ),
        error: null
      }

    case 'SET_ALL_FILTER_ITEMS':
      return {
        ...state,
        filters: state.filters.map(filter =>
          filter.type === action.payload.filterId
            ? {
              ...filter,
              items: filter.items.map(item => ({
                ...item,
                value: action.payload.value
              }))
            }
            : filter
        ),
        error: null
      }

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: state.filters.map(filter => ({
          ...filter,
          items: filter.items.map(item => ({ ...item, value: false }))
        })),
        error: null
      }

    case 'SET_FUNNEL_PRICING_PENDING': {
      const { funnelHash } = action.payload
      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel =>
          funnel.hash === funnelHash
            ? {
              ...funnel,
              pricingStatus: ComparateurPricingStatus.Loading,
              pricing: undefined
            }
            : funnel
        ),
        error: null
      }
    }

    case 'UPDATE_CONTRACT_FUNNEL_FEE': {
      const { funnelHash, feeCode, newValue } = action.payload
      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel =>
          funnel.hash === funnelHash
            ? {
              ...funnel,
              fees: funnel.fees?.map((fee: FeesDto) =>
                fee.code === feeCode
                  ? { ...fee, value: newValue }
                  : fee
              )
            }
            : funnel
        ),
        error: null
      }
    }

    case 'UPDATE_CONTRACT_FUNNEL_GUARANTEES': {
      const { contractFunnelId, contractIdentifier, newGuarantees } = action.payload
      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel =>
          funnel.id === contractFunnelId && funnel.contractIdentifier === contractIdentifier
            ? { ...funnel, guarantees: newGuarantees }
            : funnel
        ),
        error: null
      }
    }

    case 'SYNC_GLOBAL_FEES_TO_FUNNELS': {
      const { fees: newGlobalFees } = action.payload

      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel => {
          // Ne pas toucher les funnels en mode édition (géré par le back)
          if (funnel.isEdition) return funnel

          return {
            ...funnel,
            fees: funnel.fees?.map((fee: FeesDto) =>
              fee.code in newGlobalFees ? { ...fee, value: newGlobalFees[fee.code] } : fee
            )
          }
        }),
        error: null
      }
    }

    case 'SYNC_LOANS_CONFIGURATIONS': {
      const loanIds = action.payload
      return {
        ...state,
        loansConfigurations: state.loansConfigurations.filter(config =>
          loanIds.includes(config.insuranceConfig.borrowerLoanId)
        ),
        error: null
      }
    }

    case 'UPDATE_FUNNEL_PRICING': {
      const { oldHash, newHash, borrowers, warningMessages, status, pricing, mncapBorrowerId, errorMessage, isEdition, appliedFees } = action.payload
      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel => {
          if (funnel.hash !== oldHash) return funnel

          return {
            ...funnel,
            hash: newHash,
            pricingStatus: status,
            pricing: pricing ?? funnel.pricing,
            mncapBorrowerId: mncapBorrowerId ?? funnel.mncapBorrowerId,
            borrowers: borrowers ?? funnel.borrowers,
            warningMessages: warningMessages
              ? warningMessages
              : funnel.warningMessages,
            errorMessage: errorMessage ?? funnel.errorMessage,
            isEdition: isEdition ?? funnel.isEdition,
            fees: appliedFees
              ? funnel.fees?.map((fee: FeesDto) => {
                  const applied = appliedFees.find(af => af.code === fee.code)
                  return applied ? { ...fee, value: applied.value } : fee
                })
              : funnel.fees
          }
        }),
        error: null
      }
    }

    case 'MARK_PENDING_FUNNELS_TIMEOUT':
      return {
        ...state,
        contractFunnels: state.contractFunnels.map(funnel =>
          funnel.pricingStatus === ComparateurPricingStatus.Pending
            || funnel.pricingStatus === ComparateurPricingStatus.Loading
            ? { ...funnel, pricingStatus: ComparateurPricingStatus.Timeout, errorMessage: action.payload }
            : funnel
        )
      }

    case 'SET_PRICE_LOADING':
      return { ...state, isPriceLoading: action.payload }
    case 'SET_CONTRACT_FUNNELS_LOADING':
      return { ...state, isContractFunnelsLoading: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false }
    case 'RESET':
      return initialState
    default:
      return state
  }
}
